import { useState, useRef, useEffect } from "react";
import { Mic, Send, Square, ImagePlus, X } from "lucide-react";

export default function ChatInput({ onSend, onTranscribe, onImageUpload, isLoading }) {
  const [text, setText] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      textarea.style.height = Math.min(textarea.scrollHeight, 128) + "px";
    }
  }, [text]);

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (imageFile && onImageUpload) {
      onImageUpload(imageFile, text.trim());
      setText("");
      setPreviewImage(null);
      setImageFile(null);
      if (textareaRef.current) textareaRef.current.style.height = "auto";
      return;
    }
    if (text.trim() && !isLoading) {
      onSend(text.trim());
      setText("");
      if (textareaRef.current) textareaRef.current.style.height = "auto";
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      alert("Please select an image file (JPG, PNG, etc.)");
      return;
    }
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPreviewImage(ev.target.result);
    reader.readAsDataURL(file);
  };

  const clearImage = () => {
    setImageFile(null);
    setPreviewImage(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
          ? "audio/webm;codecs=opus"
          : "audio/webm",
      });
      chunksRef.current = [];
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach((track) => track.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (blob.size > 0) {
          setIsTranscribing(true);
          const transcribedText = await onTranscribe(blob);
          setIsTranscribing(false);
          if (transcribedText) setText((prev) => prev + transcribedText);
        }
      };
      mediaRecorder.start();
      mediaRecorderRef.current = mediaRecorder;
      setIsRecording(true);
    } catch (err) {
      alert("Microphone access was denied. Please allow microphone permissions to use voice input.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const handleMicClick = () => {
    if (isRecording) stopRecording();
    else startRecording();
  };

  const canSend = (text.trim().length > 0 || imageFile) && !isLoading;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 bg-white/80 backdrop-blur-xl border-t border-gray-100 p-4 pb-6"
      data-testid="chat-input-area"
    >
      <div className="max-w-3xl mx-auto">
        {/* Image Preview */}
        {previewImage && (
          <div className="mb-3 relative inline-block" data-testid="image-preview-container">
            <img
              src={previewImage}
              alt="Upload preview"
              className="h-20 w-20 object-cover rounded-xl border border-gray-200 shadow-sm"
              data-testid="image-preview"
            />
            <button
              onClick={clearImage}
              className="absolute -top-2 -right-2 w-5 h-5 bg-black text-white rounded-full flex items-center justify-center cursor-pointer hover:bg-gray-700 transition-colors"
              data-testid="clear-image-btn"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="relative flex items-end gap-2">
          {/* Mic Button */}
          <button
            type="button"
            onClick={handleMicClick}
            disabled={isTranscribing}
            className={`h-10 w-10 rounded-full flex items-center justify-center transition-all duration-200 flex-shrink-0 ${
              isRecording
                ? "bg-red-500 text-white animate-pulse-ring"
                : "bg-transparent text-gray-500 hover:text-gray-800 hover:bg-gray-100"
            } ${isTranscribing ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
            data-testid="mic-button"
            aria-label={isRecording ? "Stop recording" : "Start voice input"}
          >
            {isRecording ? (
              <Square className="w-4 h-4 fill-current" />
            ) : (
              <Mic className="w-5 h-5" />
            )}
          </button>

          {/* Image Upload Button */}
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isLoading}
            className="h-10 w-10 rounded-full flex items-center justify-center transition-all duration-200 flex-shrink-0 bg-transparent text-gray-500 hover:text-gray-800 hover:bg-gray-100 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="image-upload-btn"
            aria-label="Upload image"
          >
            <ImagePlus className="w-5 h-5" />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
            data-testid="file-input"
          />

          {/* Input Wrapper */}
          <div className="relative w-full bg-gray-50 border border-gray-200 rounded-3xl flex items-end shadow-sm focus-within:ring-2 focus-within:ring-black/5 focus-within:border-gray-300 transition-all">
            <textarea
              ref={textareaRef}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={
                isTranscribing
                  ? "Transcribing your voice..."
                  : imageFile
                  ? "Add a message (optional)..."
                  : "Ask anything"
              }
              rows={1}
              disabled={isLoading || isTranscribing}
              className="w-full bg-transparent border-0 focus:ring-0 focus:outline-none resize-none max-h-32 min-h-[44px] py-3 px-5 text-[15px] placeholder:text-gray-400"
              data-testid="chat-input"
            />
          </div>

          {/* Send Button */}
          <button
            type="submit"
            disabled={!canSend}
            className={`h-10 w-10 rounded-full flex items-center justify-center transition-all duration-200 flex-shrink-0 ${
              canSend
                ? "bg-black text-white hover:bg-gray-800 cursor-pointer"
                : "bg-gray-200 text-gray-400 cursor-not-allowed"
            }`}
            data-testid="send-button"
            aria-label="Send message"
          >
            <Send className="w-4 h-4" />
          </button>
        </form>

        {/* Status */}
        {isRecording && (
          <p className="text-xs text-red-500 font-medium text-center mt-2 animate-pulse" data-testid="recording-status">
            Recording... Click the stop button when done
          </p>
        )}
        {isTranscribing && (
          <p className="text-xs text-gray-500 font-medium text-center mt-2" data-testid="transcribing-status">
            Transcribing your voice...
          </p>
        )}
      </div>
    </div>
  );
}
